// =============================================================================
// MFASetup — opt-in TOTP enrollment flow for desktop sellers + customers.
//   1. Intro    → explains 2FA + suggested authenticator apps + Start button
//   2. QR code  → renders Supabase enroll() QR + manual setup key + 6-digit verify
//   3. Recovery → shows the 10 one-time recovery codes, gated behind "I've saved"
//
// Mirrors kapio-admin/src/components/AdminMFASetup.jsx but desktop-styled and
// reachable from the SecurityPanel (Privacy & Security tab) — not mandatory,
// just a nudge for sellers who want extra protection on their earnings.
//
// IMPORTANT: codes from generate_recovery_codes() are returned exactly once.
// The UI gates "Done" behind a "I've saved my codes" checkbox to make the
// one-shot nature of the codes obvious.
// =============================================================================

import { useState } from "react";
import { ShieldCheck, Smartphone, Copy, Check, AlertTriangle } from "lucide-react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Icon } from "../components/Icon";
import { supabase } from "../lib/supabase";

// Format a base32 TOTP secret in groups of 4 chars for readability —
// "ABCD1234EFGH5678" → "ABCD 1234 EFGH 5678".
function formatSecret(secret) {
  return secret.replace(/\s+/g, "").match(/.{1,4}/g)?.join(" ") ?? secret;
}

export function MFASetup({ onClose, onComplete }) {
  const [step, setStep] = useState("intro"); // intro | qr | recovery
  const [factorId, setFactorId] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // ── Step 1 → 2: enroll ─────────────────────────────────────────────────
  const startEnroll = async () => {
    setLoading(true);
    setError("");
    // Defensive: previous unverified factors block enroll() with "factor
    // already exists". Multiple can accumulate across aborted attempts,
    // so loop over ALL of them rather than just the first. Filter by
    // `!== "verified"` instead of `=== "unverified"` so we don't miss
    // any future Supabase states (pending, expired, etc.). Verified
    // factors stay intact — the user may have enrolled on another
    // device and we shouldn't break that.
    try {
      const { data: existing } = await supabase.auth.mfa.listFactors();
      const stale = (existing?.totp ?? []).filter((f) => f.status !== "verified");
      for (const f of stale) {
        try {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        } catch {
          /* best-effort cleanup */
        }
      }
    } catch {
      /* listFactors failure is non-blocking — let enroll() error surface below */
    }

    const { data, error: err } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      // `issuer` is what appears as the "site" / app name in the user's
      // authenticator app. Without it, Supabase falls back to the project
      // Site URL ("kapio-apps.pages.dev") — confusing for sellers who may
      // also enroll on mobile or admin. "Kapio Seller" makes the entry
      // distinguishable from the other Kapio apps.
      issuer: "Kapio Seller",
      friendlyName: `Kapio Seller (${new Date().toLocaleDateString()})`,
    });
    setLoading(false);
    if (err || !data) {
      setError(err?.message || "Failed to start setup. Please try again.");
      return;
    }
    setFactorId(data.id);
    // Defensive QR data-URI wrap — same logic as mobile/admin. Some Supabase
    // versions ship a data: URI already, others ship raw SVG.
    const rawQr = data.totp?.qr_code ?? "";
    setQrCode(
      rawQr.startsWith("data:")
        ? rawQr
        : rawQr
          ? `data:image/svg+xml;utf8,${encodeURIComponent(rawQr)}`
          : ""
    );
    setSecret(data.totp?.secret ?? "");
    setStep("qr");
  };

  // ── Step 2 → 3: verify code + generate recovery ────────────────────────
  // challengeAndVerify is a single round trip — the older challenge() then
  // verify() pattern raced against region replicas and produced a generic
  // "Could not start verification" failure mid-enrollment.
  const verifyCode = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    setError("");
    const { error: vErr } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code,
    });
    if (vErr) {
      setLoading(false);
      const msg = (vErr.message || "").toLowerCase();
      if (msg.includes("invalid")) {
        setError("Invalid code. Make sure your authenticator clock is in sync.");
      } else {
        setError(vErr.message || "Verification failed. Please try again.");
      }
      return;
    }
    // Verified — generate recovery codes. If this fails, unenroll the factor
    // we just verified so the user isn't stuck with MFA but no recovery
    // path. Consistent state matters more than convenience here.
    const { data: codes, error: gErr } = await supabase.rpc("generate_recovery_codes");
    if (gErr || !codes) {
      try {
        await supabase.auth.mfa.unenroll({ factorId });
      } catch {
        /* best-effort cleanup */
      }
      setLoading(false);
      setError("Could not generate recovery codes. Please try again.");
      return;
    }
    setRecoveryCodes(codes);
    setLoading(false);
    setStep("recovery");
  };

  const copySecret = async () => {
    if (!secret) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(secret);
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2000);
      }
    } catch {
      /* clipboard write can fail silently in private mode — non-critical */
    }
  };

  const copyAll = async () => {
    const blob = recoveryCodes.join("\n");
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(blob);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      /* clipboard write can fail silently in private mode — non-critical */
    }
  };

  const handleContinue = () => {
    if (!confirmed) return;
    onComplete?.();
  };

  return (
    <div style={{ maxWidth: 540, margin: "0 auto" }}>
      {/* Back row mirrors the rest of the desktop screens */}
      <div style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 12 }}>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              color: C.muted,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <Icon name="chevron-left" size={14} color={C.muted} />
            Back to security
          </button>
        )}
        <h1
          style={{
            fontSize: 18,
            fontWeight: 900,
            color: C.charcoal,
            margin: 0,
            letterSpacing: "-0.3px",
          }}
        >
          Two-factor authentication
        </h1>
      </div>

      <Card pad={28}>
        {/* ── Step 1: Intro ────────────────────────────────────────────── */}
        {step === "intro" && (
          <>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 16 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 18,
                  background: C.greenBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                <ShieldCheck size={32} color={C.hunter} />
              </div>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: C.charcoal,
                  margin: "0 0 6px",
                  textAlign: "center",
                  letterSpacing: "-0.3px",
                }}
              >
                Add an extra layer of security
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: C.muted,
                  margin: 0,
                  textAlign: "center",
                  lineHeight: 1.5,
                }}
              >
                Two-factor authentication asks for a 6-digit code from your
                authenticator app every time you sign in.
              </p>
            </div>

            <div
              style={{
                background: C.cream,
                borderRadius: 14,
                padding: 14,
                marginBottom: 16,
                border: `1px solid ${C.dustDk}50`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Smartphone size={14} color={C.charcoal} />
                <span style={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}>
                  You'll need an authenticator app
                </span>
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: C.muted,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Google Authenticator, Authy, or 1Password all work. Install
                one on your phone or desktop before continuing.
              </p>
            </div>

            {error && (
              <div
                style={{
                  background: C.redBg,
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 14,
                  border: `1px solid ${C.red}20`,
                }}
              >
                <p style={{ fontSize: 12, color: C.red, margin: 0, fontWeight: 600 }}>
                  {error}
                </p>
              </div>
            )}

            <Btn onClick={startEnroll} disabled={loading} full size="lg">
              {loading ? "Starting…" : "Start setup"}
            </Btn>
          </>
        )}

        {/* ── Step 2: QR + verify ────────────────────────────────────── */}
        {step === "qr" && (
          <>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 800,
                margin: "0 0 6px",
                textAlign: "center",
                color: C.charcoal,
              }}
            >
              Scan the QR code
            </h2>
            <p
              style={{
                fontSize: 13,
                color: C.muted,
                margin: "0 0 16px",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              Open your authenticator app, tap "+" to add an account, then
              scan the code below.
            </p>

            {qrCode && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
                <div
                  style={{
                    background: C.white,
                    padding: 12,
                    borderRadius: 16,
                    border: `1px solid ${C.dustDk}`,
                  }}
                >
                  <img
                    src={qrCode}
                    alt="2FA QR code"
                    style={{ width: 200, height: 200, display: "block" }}
                  />
                </div>
              </div>
            )}

            {/* ── Manual setup key — for users on the same machine ────── */}
            {secret && (
              <div style={{ marginBottom: 16 }}>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.muted,
                    textAlign: "center",
                    margin: "0 0 6px",
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                  }}
                >
                  Or enter this key manually
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: C.cream,
                    borderRadius: 10,
                    border: `1px solid ${C.dustDk}80`,
                    padding: "10px 12px",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontWeight: 600,
                      color: C.charcoal,
                      letterSpacing: 0.5,
                      wordBreak: "break-all",
                    }}
                    aria-label="Manual setup key"
                  >
                    {formatSecret(secret)}
                  </span>
                  <button
                    type="button"
                    onClick={copySecret}
                    aria-label="Copy manual setup key"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 8px",
                      borderRadius: 8,
                      background: secretCopied ? C.greenBg : C.white,
                      border: `1px solid ${secretCopied ? C.hunter : C.dustDk}`,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: 11,
                      fontWeight: 700,
                      color: secretCopied ? C.hunter : C.charcoal,
                    }}
                  >
                    {secretCopied ? (
                      <>
                        <Check size={12} color={C.hunter} strokeWidth={3} />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={12} color={C.charcoal} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                {/* Manual-entry parameter hint */}
                <p
                  style={{
                    fontSize: 11,
                    color: C.muted,
                    margin: "8px 0 0",
                    lineHeight: 1.5,
                  }}
                >
                  If your app asks: choose{" "}
                  <strong style={{ color: C.charcoal, fontWeight: 600 }}>Time-based (TOTP)</strong>,{" "}
                  <strong style={{ color: C.charcoal, fontWeight: 600 }}>6 digits</strong>,{" "}
                  <strong style={{ color: C.charcoal, fontWeight: 600 }}>30-second period</strong>, and{" "}
                  <strong style={{ color: C.charcoal, fontWeight: 600 }}>SHA-1</strong>. Click{" "}
                  <strong style={{ color: C.charcoal, fontWeight: 600 }}>Copy</strong> rather than typing —
                  the spaces above are just for reading.
                </p>
              </div>
            )}

            <p
              style={{
                fontSize: 12,
                color: C.muted,
                margin: "0 0 6px",
                textAlign: "center",
              }}
            >
              Then enter the 6-digit code your app shows
            </p>

            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                if (error) setError("");
              }}
              onKeyDown={(e) => e.key === "Enter" && code.length === 6 && verifyCode()}
              placeholder="000000"
              autoFocus
              maxLength={6}
              aria-label="6-digit verification code"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: `1.5px solid ${C.dustDk}`,
                fontSize: 16,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                letterSpacing: 4,
                textAlign: "center",
                outline: "none",
                background: C.cream,
                boxSizing: "border-box",
                marginBottom: 14,
              }}
            />

            {error && (
              <div
                style={{
                  background: C.redBg,
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 14,
                  border: `1px solid ${C.red}20`,
                }}
              >
                <p style={{ fontSize: 12, color: C.red, margin: 0, fontWeight: 600 }}>
                  {error}
                </p>
              </div>
            )}

            <Btn onClick={verifyCode} disabled={loading || code.length !== 6} full size="lg">
              {loading ? "Verifying…" : "Verify"}
            </Btn>
          </>
        )}

        {/* ── Step 3: Recovery codes ─────────────────────────────────── */}
        {step === "recovery" && (
          <>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 800,
                margin: "0 0 6px",
                textAlign: "center",
                color: C.charcoal,
              }}
            >
              Save your recovery codes
            </h2>
            <p
              style={{
                fontSize: 13,
                color: C.muted,
                margin: "0 0 16px",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              Use one of these if you lose access to your authenticator app.
              Each code works once.
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                background: C.amberBg,
                borderRadius: 12,
                padding: 12,
                marginBottom: 16,
                border: `1px solid ${C.amberBorder}`,
              }}
            >
              <AlertTriangle size={16} color={C.amberDk} style={{ flexShrink: 0, marginTop: 1 }} />
              <p
                style={{
                  flex: 1,
                  fontSize: 12,
                  color: C.amberDk,
                  margin: 0,
                  lineHeight: 1.5,
                  fontWeight: 500,
                }}
              >
                Save these codes somewhere safe. They will not be shown
                again. Each code can be used once if you lose your
                authenticator.
              </p>
            </div>

            <div
              style={{
                background: C.cream,
                borderRadius: 14,
                padding: "14px 16px",
                marginBottom: 14,
                border: `1px solid ${C.dustDk}`,
              }}
            >
              {recoveryCodes.map((rc, i) => (
                <p
                  key={i}
                  style={{
                    fontSize: 15,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontWeight: 600,
                    color: C.charcoal,
                    letterSpacing: 1.5,
                    padding: "4px 0",
                    margin: 0,
                    textAlign: "center",
                    userSelect: "all",
                  }}
                >
                  {rc}
                </p>
              ))}
            </div>

            <button
              onClick={copyAll}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "12px 0",
                borderRadius: 12,
                background: C.cream,
                border: `1px solid ${C.dustDk}`,
                cursor: "pointer",
                marginBottom: 16,
                fontFamily: "inherit",
              }}
            >
              {copied ? (
                <>
                  <Check size={14} color={C.hunter} strokeWidth={3} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.hunter }}>
                    Copied
                  </span>
                </>
              ) : (
                <>
                  <Copy size={14} color={C.charcoal} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}>
                    Copy all
                  </span>
                </>
              )}
            </button>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                style={{
                  width: 18,
                  height: 18,
                  accentColor: C.hunter,
                  cursor: "pointer",
                }}
              />
              <span style={{ flex: 1, fontSize: 13, color: C.charcoal }}>
                I have saved these codes in a safe place
              </span>
            </label>

            <Btn onClick={handleContinue} disabled={!confirmed} full size="lg">
              Done
            </Btn>
          </>
        )}
      </Card>
    </div>
  );
}
